import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError } from '../../api';
import { quickbooksEndpoints } from '../../api';
import { Button, Panel } from '../../ui';
import { INPUT } from './shared';

/**
 * The QuickBooks project this event's money goes to (D-326).
 *
 * Linked once, every income and expense line of the event is posted tagged
 * with the project, so the project's profit and loss in QuickBooks fills in on
 * its own. Anything already posted stays as it was; anything waiting to go is
 * redone with the new project.
 */
export function QuickBooksProjectCard({ eventId, can, onError }: { eventId: string; can: (permission: string) => boolean; onError: (error: ApiError) => void }) {
  const queryClient = useQueryClient();
  const editable = can('finance.accounting_mappings.manage');
  const [choosing, setChoosing] = useState(false);
  const [choice, setChoice] = useState('');

  const current = useQuery({ queryKey: ['qbo-project', eventId], queryFn: () => quickbooksEndpoints.eventProject(eventId), enabled: can('finance.reconciliation.view') });
  // Asked of QuickBooks only when somebody is actually choosing.
  const projects = useQuery({ queryKey: ['qbo-projects'], queryFn: quickbooksEndpoints.projects, enabled: choosing, staleTime: 60_000 });
  const done = () => {
    setChoosing(false);
    setChoice('');
    void queryClient.invalidateQueries({ queryKey: ['qbo-project', eventId] });
  };
  const link = useMutation({ mutationFn: () => quickbooksEndpoints.linkEventProject(eventId, choice), onSuccess: done, onError });
  const unlink = useMutation({ mutationFn: () => quickbooksEndpoints.unlinkEventProject(eventId), onSuccess: done, onError });

  if (!can('finance.reconciliation.view')) return null;
  const project = current.data?.project ?? null;
  const list = projects.data?.projects ?? [];

  return (
    <Panel
      title="QuickBooks project"
      description="The project this event's income and expenses are posted to. Ticket sales, fees, discounts, comps and costs go to the project; bank, clearing and payable lines do not. Journals already posted are not changed; anything still waiting to go is updated."
      actions={
        editable && !choosing ? (
          <div className="flex gap-2">
            <Button onClick={() => setChoosing(true)}>{project ? 'Change' : 'Link a project'}</Button>
            {project ? (
              <Button onClick={() => unlink.mutate()} disabled={unlink.isPending}>
                Unlink
              </Button>
            ) : null}
          </div>
        ) : null
      }
    >
      <div className="px-5 py-4 text-sm">
        {project ? (
          <p>
            <span className="font-semibold text-ink">{project.qbo_project_name_snapshot}</span>
            <span className="ml-2 text-xs text-ink-400">linked {project.linked_at.slice(0, 10)}</span>
          </p>
        ) : (
          <p className="text-ink-500">Not linked. This event's lines are posted with everything else's.</p>
        )}
        {choosing ? (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {projects.isLoading ? <span className="text-ink-500">Asking QuickBooks…</span> : null}
            {projects.isError ? <span className="text-bad">{(projects.error as ApiError).message}</span> : null}
            {projects.isSuccess ? (
              list.length === 0 ? (
                <span className="text-ink-500">QuickBooks has no projects yet. Create one there, then come back.</span>
              ) : (
                <select value={choice} onChange={(e) => setChoice(e.target.value)} className={`min-w-72 ${INPUT}`} aria-label="Project">
                  <option value="">Choose a project</option>
                  {list.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                      {p.is_project ? '' : ' (sub-customer)'}
                    </option>
                  ))}
                </select>
              )
            ) : null}
            <Button variant="primary" onClick={() => link.mutate()} disabled={!choice || link.isPending}>
              {link.isPending ? 'Linking…' : 'Link'}
            </Button>
            <Button onClick={() => setChoosing(false)}>Cancel</Button>
          </div>
        ) : null}
      </div>
    </Panel>
  );
}
